import React from "react";
import { Link } from "react-router-dom";

import SEO from "../../seo/SEO";

import {
  Users,
  ArrowRight,
  CheckCircle2,
  Database,
  ShieldCheck,
  LayoutDashboard,
  Cloud,
  LineChart,
} from "lucide-react";

import "./CRMSoftwareDevelopment.css";


/* =========================================================
   CRM SERVICES
========================================================= */

const crmServices = [
  {
    icon: <Users size={28} />,

    title: "Customer Management",

    description:
      "Centralized CRM systems for managing leads, customers, contacts, communication history and customer lifecycle workflows.",
  },

  {
    icon: <LayoutDashboard size={28} />,

    title: "Sales Dashboard",

    description:
      "Advanced dashboards with realtime sales tracking, pipeline monitoring, reporting systems and business analytics.",
  },

  {
    icon: <Database size={28} />,

    title: "Lead & Data Management",

    description:
      "Scalable CRM database architecture with lead capture systems, customer segmentation and intelligent workflows.",
  },

  {
    icon: <Cloud size={28} />,

    title: "Cloud-Based CRM",

    description:
      "Secure cloud-ready CRM software with scalable backend infrastructure, API integrations and remote accessibility.",
  },

  {
    icon: <LineChart size={28} />,

    title: "Business Analytics",

    description:
      "Data-driven CRM reporting with customer insights, sales forecasting, KPI tracking and operational intelligence.",
  },

  {
    icon: <ShieldCheck size={28} />,

    title: "Secure Access Control",

    description:
      "Enterprise-grade authentication, role-based access management and secure customer data protection systems.",
  },
];


/* =========================================================
   TECHNOLOGY STACK
========================================================= */

const technologies = [
  "React.js",
  "Next.js",
  "FastAPI",
  "Node.js",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "Docker",
  "AWS",
  "Firebase",
];


/* =========================================================
   CRM SOFTWARE DEVELOPMENT PAGE
========================================================= */

export default function CRMSoftwareDevelopment() {

  /* =======================================================
     CANONICAL URL
  ======================================================= */

  const canonicalUrl =
    "https://www.bairagi-consultants.com/crm-software-development";


  /* =======================================================
     SERVICE STRUCTURED DATA
  ======================================================= */

  const serviceSchema = {
    "@context": "https://schema.org",

    "@type": "Service",

    name: "CRM Software Development Services",

    serviceType:
      "Custom CRM Software Development",

    url: canonicalUrl,

    description:
      "Custom CRM software development for lead management, customer relationship management, sales pipelines, dashboards, analytics, automation and cloud-based business workflows.",

    provider: {
      "@type": "Organization",

      name: "Bairagi Consultants",

      url: "https://www.bairagi-consultants.com",
    },

    areaServed: {
      "@type": "Country",

      name: "India",
    },
  };


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="Custom CRM Software Development Company | Bairagi Consultants"
        description="Custom CRM software development for lead management, sales tracking, customer workflows, dashboards, analytics, automation and cloud-based business operations."
        path="/crm-software-development"
        schema={serviceSchema}
      />


      <div className="crm-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="crm-hero crm-container">

          {/* LEFT */}

          <div className="crm-hero-content">

            <span className="crm-badge">
              Custom CRM Software Development
            </span>


            <h1 className="crm-title">

              Smart

              <span>
                {" "}CRM Software
              </span>

              <br />

              For Modern Businesses

            </h1>


            <p className="crm-description">

              We build custom CRM software solutions
              for lead management, customer tracking,
              sales pipelines, follow-ups, dashboards,
              analytics and business automation.

            </p>


            {/* ACTIONS */}

            <div className="crm-actions">

              <Link
                to="/contact?service=crm"
                className="crm-primary-btn"
              >

                Start CRM Project

                <ArrowRight size={18} />

              </Link>


              <Link
                to="/case-studies"
                className="crm-secondary-btn"
              >

                View CRM Solutions

              </Link>

            </div>

          </div>


          {/* RIGHT */}

          <div className="crm-hero-card">

            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1400&auto=format&fit=crop"
              alt="Custom CRM software development dashboard and customer management system"
            />

          </div>

        </section>


        {/* =================================================
            SERVICES
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-section-header">

            <h2>
              CRM Software
              <br />
              Development Services
            </h2>


            <p>

              Custom CRM systems designed for customer
              management, sales operations, lead tracking,
              automation, analytics and scalable business
              growth.

            </p>

          </div>


          <div className="crm-services-grid">

            {crmServices.map((service, index) => (

              <div
                key={index}
                className="crm-service-card"
              >

                {service.icon}

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            BUSINESS USE CASES
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-section-header">

            <h2>
              CRM Solutions
              <br />
              For Business Operations
            </h2>


            <p>

              A custom CRM can connect sales,
              customer service, follow-ups,
              management reporting and operational
              workflows within one centralized
              business platform.

            </p>

          </div>


          <div className="crm-services-grid">

            <div className="crm-service-card">

              <Users size={28} />

              <h3>
                Lead Management
              </h3>

              <p>
                Capture enquiries, assign leads,
                track follow-ups and manage the
                complete sales lifecycle.
              </p>

            </div>


            <div className="crm-service-card">

              <LayoutDashboard size={28} />

              <h3>
                Sales Pipeline Management
              </h3>

              <p>
                Track opportunities, deal stages,
                sales activity and business
                conversion performance.
              </p>

            </div>


            <div className="crm-service-card">

              <LineChart size={28} />

              <h3>
                MIS & CRM Reporting
              </h3>

              <p>
                Generate dashboards and reports
                for leads, customers, sales,
                performance and management decisions.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            TECH STACK
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-section-header">

            <h2>
              CRM Development
              <br />
              Technology Stack
            </h2>


            <p>

              Modern frontend, backend, database
              and cloud technologies for scalable
              custom CRM software development.

            </p>

          </div>


          <div className="crm-tech-stack">

            {technologies.map((tech) => (

              <span
                key={tech}
                className="crm-tech-tag"
              >

                {tech}

              </span>

            ))}

          </div>

        </section>


        {/* =================================================
            CASE STUDY
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-case-study">

            <span>
              Featured CRM Platform
            </span>


            <h2>
              Enterprise CRM Ecosystem
            </h2>


            <p>

              We designed and developed a scalable
              CRM ecosystem with customer onboarding,
              lead management, sales pipeline tracking,
              analytics dashboards, automated workflows
              and realtime business intelligence systems.

            </p>


            <Link
              to="/case-studies"
              className="crm-secondary-btn"
            >

              Explore Case Studies

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>


        {/* =================================================
            WHY CHOOSE US
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-section-header">

            <h2>
              Why Choose Bairagi Consultants
              <br />
              for CRM Development?
            </h2>

          </div>


          <div className="crm-services-grid">

            <div className="crm-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Custom Business Workflows
              </h3>


              <p>

                CRM systems are designed around
                your actual sales, customer
                management and operational
                processes.

              </p>

            </div>


            <div className="crm-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Intelligent Dashboards
              </h3>


              <p>

                Business dashboards provide
                visibility into leads, sales,
                customer activity, conversion
                and performance KPIs.

              </p>

            </div>


            <div className="crm-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Cloud-Ready Systems
              </h3>


              <p>

                Modern CRM platforms can be
                deployed with scalable APIs,
                secure databases and cloud-ready
                infrastructure.

              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            INTEGRATION
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-section-header">

            <h2>
              CRM Integration
              <br />
              With Business Systems
            </h2>


            <p>

              Custom CRM software can integrate
              with ecommerce platforms, mobile apps,
              ERP systems, MIS dashboards, email
              communication and other enterprise
              applications.

            </p>

          </div>


          <div className="crm-services-grid">

            <div className="crm-service-card">

              <Database size={28} />

              <h3>
                Central Customer Database
              </h3>

              <p>
                Maintain structured customer,
                lead, enquiry and communication
                information in one system.
              </p>

            </div>


            <div className="crm-service-card">

              <Cloud size={28} />

              <h3>
                API Integrations
              </h3>

              <p>
                Connect CRM with websites,
                ecommerce systems, mobile apps
                and other business applications.
              </p>

            </div>


            <div className="crm-service-card">

              <ShieldCheck size={28} />

              <h3>
                Role-Based Access
              </h3>

              <p>
                Provide controlled access for
                administrators, managers, sales
                teams and operational users.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="crm-section crm-container">

          <div className="crm-cta">

            <h2>

              Ready To Build Your
              <br />

              CRM Software Platform?

            </h2>


            <p>

              Let's discuss your lead management,
              customer workflows, sales process,
              reporting requirements and business
              automation needs.

            </p>


            <Link
              to="/contact?service=crm"
              className="crm-primary-btn"
            >

              Schedule Consultation

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>

      </div>

    </>
  );
}